import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, Subject } from 'rxjs';
import { AuthRequest } from '@dto/authRequest';
import { AuthResponse } from '@dto/authResponse';
import { ForgotPasswordRequest } from '@dto/forgotPasswordRequest';
import { OnboardingRequest } from '@dto/onboardingRequest';
import { RegistrationRequest } from '@dto/registrationRequest';
import { RegistrationResponse } from '@dto/registrationResponse';
import { ResetPasswordRequest } from '@dto/resetPasswordRequest';
import { CustomEncoder } from '../custom-encoder';
import { User } from '@models/identity/user';
import { SendInviteRequest } from '@dto/sendInviteRequest';
import { SendInviteResponse } from '@dto/sendInviteResponse';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TokenMessage } from '@dto/tokenMessage';
import { tap } from 'rxjs/operators';
import { UsersService } from '@services/entity/users.service';
import { UserDto } from '@dto/userDto';
import { PaymentsService } from '@services/payments/payments.service';
import { ClaimRfidCardRequest } from '@dto/claimRfidCardRequest';
import { Roles } from '@enums/roles.enum';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  private authChangeSub = new Subject<boolean>();
  public authChanged$ = this.authChangeSub.asObservable();

  private currentUserSub = new Subject<UserDto>();
  public currentUserLoaded$ = this.currentUserSub.asObservable();

  private currentUser: UserDto;
  private rolesOfCurrentUser: string[];
  constructor(
    @Inject('BASE_URL') private baseUrl: string,
    private http: HttpClient,
    private jwtHelper: JwtHelperService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private usersService: UsersService,
    private paymentsService: PaymentsService) {
    this.authChanged$.subscribe(result => {
      this.onAuthChanged(result);
    })
  }

  public sendAuthStateChangeNotification(isAuthenticated: boolean) {
    if (isAuthenticated) {
      this.authChangeSub.next(isAuthenticated);
    }
  }

  private async onAuthChanged(isAuthenticated: boolean) {
    if (isAuthenticated) {
      this.currentUser = await this.usersService.getLoggedInUserAsync();
      this.rolesOfCurrentUser = await this.usersService.getLoggedInUserRolesForSelectedCustomerAsync();
      this.currentUserSub.next(this.currentUser);
    }
  }

  public getRolesOfCurrentUser() {
    return this.rolesOfCurrentUser;
  }

  public getLoggedInUser(): UserDto {
    return this.currentUser;
  }

  public getLoggedInUserSubject(): Observable<UserDto> {
    return this.currentUserLoaded$;
  }

  /**
  * @deprecated The method should not be used
  */
  public getLoggedInUserAsObservable() {
    return this.http.get<User>(this.baseUrl + 'api/auth/user');
  }

  getUserByEmailForRegistration(email: string, token: string): Observable<UserDto> {
    let params = new HttpParams({ encoder: new CustomEncoder() })
    params = params.append('token', token);
    params = params.append('email', email);
    return this.http.get<UserDto>(this.baseUrl + 'api/auth/userByEmail', { params: params });
  }

  registerUser(registrationRequest: RegistrationRequest) {
    return this.http.post<RegistrationResponse>(this.baseUrl + 'api/auth/register', registrationRequest);
  }

  registerUserFromOperator(registrationRequest: RegistrationRequest) {
    return this.http.post<RegistrationResponse>(this.baseUrl + 'api/auth/register-from-operator', registrationRequest);
  }

  registerOperatorFromOperator(registrationRequest: RegistrationRequest) {
    return this.http.post<RegistrationResponse>(this.baseUrl + 'api/auth/register-operator-from-operator', registrationRequest);
  }

  registerUserWithInvite(registrationRequest: RegistrationRequest) {
    return this.http.post<RegistrationResponse>(this.baseUrl + 'api/auth/register-with-invite', registrationRequest);
  }

  loginUser(authRequest: AuthRequest) {
    return this.http.post<AuthResponse>(this.baseUrl + 'api/auth/login', authRequest);
  }

  confirmEmail(token: string, email: string) {
    let params = new HttpParams({ encoder: new CustomEncoder() })
    params = params.append('token', token);
    params = params.append('email', email);
    return this.http.get(this.baseUrl + 'api/auth/emailconfirmation', { params: params });
  }

  confirmEmailforCustomer(customerId: number) {
    return this.http.post(this.baseUrl + 'api/customers/' + customerId + '/emailconfirmation', null);
  }

  confirmEmailFromOnboarding(userId: string) {
    return this.http.get(this.baseUrl + 'api/auth/' + userId + '/emailConfirmOnboarding');
  }

  confirmEmailChange(token: string, email: string, newEmail: string) {
    let params = new HttpParams({ encoder: new CustomEncoder() })
    params = params.append('token', token);
    params = params.append('email', email);
    params = params.append('newEmail', newEmail);
    return this.http.get(this.baseUrl + 'api/auth/emailChangeConfirmation', { params: params });
  }

  forgotPassword(forgotPasswordRequest: ForgotPasswordRequest) {
    return this.http.post(this.baseUrl + 'api/auth/forgotpassword', forgotPasswordRequest);
  }

  activatedFromOperator(onboardingRequest: OnboardingRequest) {
    return this.http.post(this.baseUrl + 'api/auth/onboarding', onboardingRequest);
  }

  resetPassword(resetPasswordRequest: ResetPasswordRequest) {
    return this.http.post(this.baseUrl + 'api/auth/resetpassword', resetPasswordRequest);
  }

  sendInvite(sendInviteRequest: SendInviteRequest) {
    return this.http.post<SendInviteResponse>(this.baseUrl + 'api/auth/sendinvite', sendInviteRequest);
  }

  logout() {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");

    this.currentUser = null;
    this.rolesOfCurrentUser = null;
    this.currentUserSub.next(null);

    this.sendAuthStateChangeNotification(false);
  }

  public getAccessToken(): string {
    return localStorage.getItem("accessToken");
  }

  public getRefreshToken(): string {
    return localStorage.getItem("refreshToken");
  }

  public isUserAuthenticated(): boolean {
    if (this.accessTokenExists()) {
      if (this.isAccessTokenValid()) {
        return true;
      }
    }
  }

  public accessTokenExists(): boolean {
    const accessToken = this.getAccessToken();

    if (accessToken) {
      return true;
    }

    return false;
  }

  public isAccessTokenValid(): boolean {
    const accessToken = this.getAccessToken();

    if (accessToken && !this.jwtHelper.isTokenExpired(accessToken)) {
      return true;
    }

    return false;
  }

  public refreshToken() {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    
    if (accessToken && refreshToken) {
      var tokenMessage: TokenMessage = {
        accessToken: accessToken,
        refreshToken: refreshToken,
        isRefreshSuccessful: false
      }
      
      return this.http.post<any>(this.baseUrl + 'api/auth/refresh', tokenMessage).pipe(tap((result: TokenMessage) => {
        localStorage.setItem("accessToken", result.accessToken);
        localStorage.setItem("refreshToken", result.refreshToken);
      }));
    }
  }

  public async tryRefreshingTokens(): Promise<boolean> {
    const accessToken = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");

    if (!accessToken || !refreshToken) {
      return false;
    }

    this.spinner.show();

    var tokenMessage: TokenMessage = {
      accessToken: accessToken,
      refreshToken: refreshToken,
      isRefreshSuccessful: false
    }

    try {
      let result = await this.http.post<TokenMessage>(this.baseUrl + 'api/auth/refresh', tokenMessage).toPromise();
      localStorage.setItem("accessToken", result.accessToken);
      localStorage.setItem("refreshToken", result.refreshToken);
      this.sendAuthStateChangeNotification(true);
      this.spinner.hide();
      return result.isRefreshSuccessful;
    } catch (error) {
      this.spinner.hide();
      this.logout();
      this.router.navigate(['/auth/login']);
      return error.isRefreshSuccessful;
    }
  }

  public getTokenNameClaim(token: string): string {
    const decodedToken = this.jwtHelper.decodeToken(token);
    const email = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name']

    return email;
  }

  public userHasRole(role: string): boolean {
    const accessToken = this.getAccessToken();

    if (accessToken) {
      const decodedToken = this.jwtHelper.decodeToken(accessToken);
      const roles = decodedToken?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      if (roles == null) return false;
      if (Array.isArray(roles)) return roles.includes(role);
      return roles === role;
    }

    return false;
  }

  public userHasAnyRole(roles: string[]): boolean {
    const accessToken = this.getAccessToken();

    if (accessToken) {
      const decodedToken = this.jwtHelper.decodeToken(accessToken);
      const tokenRoles = decodedToken?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      if (tokenRoles == null) return false;
      if (Array.isArray(tokenRoles)) {
        return tokenRoles.some(item => roles.includes(item));
      }
      return roles.includes(tokenRoles);
    }

    return false;
  }

  public claimRfidCard(claimRfidCardRequest: ClaimRfidCardRequest) {
    return this.http.post(this.baseUrl + 'api/auth/claimRfidCard', claimRfidCardRequest);
  }



  getRole() :Roles {
    if (this.userHasRole(Roles.Customer)) {
      return Roles.Customer;
    }
    else if (this.userHasRole(Roles.Operator)) {
      return Roles.Operator;
    }
    else if (this.userHasRole(Roles.User)) {
      return Roles.User;
    }
  }

}
